import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: 'Leistungen', href: '/leistungen' },
    { name: 'Über mich', href: '/ueber-mich' },
    { name: 'Projekte', href: '/#gallery' },
    { name: 'Bewertungen', href: '/#google-reviews' },
    { name: 'Kontakt', href: '/kontakt' },
  ];

  const handleNavClick = (e, href) => {
    e.preventDefault();
    setIsMenuOpen(false);

    if (href.startsWith('/#')) {
      const sectionId = href.substring(2);
      if (location.pathname === '/') {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate(`/#${sectionId}`);
      }
    } else {
      navigate(href);
    }
  };

  return (
    <header className="fixed w-full z-50 bg-white text-primary-800 shadow-md">
      <nav className="container-custom">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <Link to="/" onClick={() => setIsMenuOpen(false)} className="flex items-center space-x-3">
              <img 
                src="/logo.png" 
                alt="Fliesen Jarek Logo" 
                className="h-12 w-auto"
              />
              <img 
                src="/Textlogo.png" 
                alt="Fliesen Jarek" 
                className="h-8 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center space-x-8 absolute left-1/2 transform -translate-x-1/2">
            {navigation.map((item) => (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => handleNavClick(e, item.href)}
                className="text-primary-800/80 hover:text-primary-800 font-medium transition-colors duration-200 text-sm cursor-pointer"
              >
                {item.name}
              </a>
            ))}
          </div>

          {/* Spacer for mobile menu button alignment */}
          <div className="hidden lg:block w-10"></div>

          {/* Animated Mobile menu button - stays on top */}
          <div className="lg:hidden relative z-50">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="relative w-8 h-8 flex flex-col justify-center items-center focus:outline-none group"
            >
              {/* Top Line */}
              <span className={`block h-0.5 w-6 transform transition-all duration-300 ease-in-out ${
                isMenuOpen 
                  ? 'bg-white group-hover:bg-white/80 rotate-45 translate-y-1.5' 
                  : 'bg-primary-800/80 group-hover:bg-primary-800 translate-y-0'
              }`}></span>
              
              {/* Middle Line */}
              <span className={`block h-0.5 w-6 transform transition-all duration-300 ease-in-out my-1 ${
                isMenuOpen 
                  ? 'bg-white group-hover:bg-white/80 opacity-0 scale-0' 
                  : 'bg-primary-800/80 group-hover:bg-primary-800 opacity-100 scale-100'
              }`}></span>
              
              {/* Bottom Line */}
              <span className={`block h-0.5 w-6 transform transition-all duration-300 ease-in-out ${
                isMenuOpen 
                  ? 'bg-white group-hover:bg-white/80 -rotate-45 -translate-y-1.5' 
                  : 'bg-primary-800/80 group-hover:bg-primary-800 translate-y-0'
              }`}></span>
            </button>
          </div>
        </div>

        {/* Fullscreen Mobile Navigation */}
        <div className={`fixed inset-0 z-40 lg:hidden transition-all duration-500 ease-in-out ${
          isMenuOpen 
            ? 'opacity-100 visible' 
            : 'opacity-0 invisible'
        }`}>
          {/* Background Overlay */}
          <div className={`absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-800 to-primary-700 transition-all duration-700 ${
            isMenuOpen ? 'scale-100' : 'scale-110'
          }`}>
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className={`w-96 h-96 rounded-full bg-white/20 absolute -top-20 -right-20 transition-all duration-1000 ${
                isMenuOpen ? 'scale-100 rotate-45' : 'scale-0 rotate-0'
              }`}></div>
              <div className={`w-64 h-64 rounded-full bg-white/10 absolute bottom-20 -left-10 transition-all duration-1000 delay-200 ${
                isMenuOpen ? 'scale-100 rotate-45' : 'scale-0 rotate-0'
              }`}></div>
              <div className={`w-48 h-48 rounded-full bg-white/15 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-1000 delay-100 ${
                isMenuOpen ? 'scale-100 rotate-90' : 'scale-0 rotate-0'
              }`}></div>
            </div>
          </div>

          {/* Logo - stays in top left */}
          <div className={`absolute top-6 left-6 z-20 transition-all duration-700 delay-300 ${
            isMenuOpen 
              ? 'opacity-100 transform translate-y-0' 
              : 'opacity-0 transform translate-y-8'
          }`}>
            <div className="flex items-center space-x-3">
              <img 
                src="/logo.png" 
                alt="Fliesen Jarek Logo" 
                className="h-12 w-auto filter brightness-0 invert"
              />
              <img 
                src="/Textlogo.png" 
                alt="Fliesen Jarek" 
                className="h-8 w-auto filter brightness-0 invert"
              />
            </div>
          </div>

          {/* Navigation Content */}
          <div className="relative z-10 flex flex-col items-center justify-center h-full px-8">
            {/* Navigation Items */}
            <nav className="flex flex-col items-center space-y-8">
              {navigation.map((item, index) => (
                <a
                  key={item.name}
                  href={item.href}
                  onClick={(e) => handleNavClick(e, item.href)}
                  className={`group text-white text-3xl font-light tracking-wide cursor-pointer transition-all duration-700 ${
                    isMenuOpen 
                      ? 'opacity-100 transform translate-y-0' 
                      : 'opacity-0 transform translate-y-8'
                  }`}
                  style={{ 
                    transitionDelay: isMenuOpen ? `${400 + index * 100}ms` : '0ms' 
                  }}
                >
                  <span className="relative inline-block">
                    {item.name}
                    {/* Animated Underline */}
                    <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full"></span>
                    {/* Hover Glow Effect */}
                    <span className="absolute inset-0 bg-white/20 blur-lg scale-0 transition-all duration-300 group-hover:scale-150 -z-10"></span>
                  </span>
                </a>
              ))}
            </nav>

            {/* Contact Info */}
            <div className={`mt-16 text-center transition-all duration-700 delay-700 ${
              isMenuOpen 
                ? 'opacity-100 transform translate-y-0' 
                : 'opacity-0 transform translate-y-8'
            }`}>
              <div className="text-white/80 text-sm">
                <p>Ihr Fliesenleger in Cuxhaven</p>
                <p className="mt-1 font-medium">20 Jahre Erfahrung • 5 Jahre Garantie</p>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header; 